import React from "react";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import { Suspense, useState, useMemo, useEffect } from "react";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import CreateAttendantForm from "../../components/createForm/CreateAttendant";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import axios from "axios";
import config from "../../config";
import { Snackbar, Alert } from "@mui/material";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

export default function SchoolBusAttendants() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [attendantData, setAttendantData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const headers = [
    { key: "Names", value: "Names" },
    { key: "Staff IDs", value: "Staff IDs" },
    { key: "Phone Numbers", value: "Staff Phone No." },
    { key: "Gender", value: "Gender" },
    { key: "Routes", value: "Routes" },
    { key: "Vehicle Numbers", value: "Vehicle No." },
    { key: "actions", value: "" },
  ];

  // Filter configuration
  const [filters, setFilters] = useState([
    {
      id: "routes",
      label: t("Routes"),
      options: [],
    },
    {
      id: "gender",
      label: t("Gender"),
      options: [
        { value: "MALE", label: t("Male") },
        { value: "FEMALE", label: t("Female") },
        { value: "OTHER", label: t("Other") },
      ],
    },
  ]);

  // Get organization ID from localStorage
  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const orgId = userState.user?.organizationId;

        if (orgId) {
          setOrganizationId(orgId);
        } else {
          setAlert({
            open: true,
            message: t("Organization ID not found"),
            severity: "error",
          });
        }
      }
    } catch (err) {
      console.error("Error reading user state:", err);
      setAlert({
        open: true,
        message: t("Failed to load user information"),
        severity: "error",
      });
    }
  }, [t]);

  // Fetch attendants data when organizationId is available
  useEffect(() => {
    if (organizationId) {
      fetchAttendants();
      fetchRoutesForFilter();
    }
  }, [organizationId]);

  const fetchRoutesForFilter = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/route/organization?organizationId=${organizationId}`,
        { withCredentials: true },
      );

      if (response.data) {
        const routes = Array.isArray(response.data) ? response.data : [];
        const routeOptions = routes.map((route) => ({
          value: route.name,
          label: route.name,
        }));

        setFilters((prevFilters) =>
          prevFilters.map((filter) =>
            filter.id === "routes"
              ? { ...filter, options: routeOptions }
              : filter,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching routes for filter:", error);
    }
  };

  const fetchAttendants = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${config.baseURL}/employee/organization`,
        {
          params: { organizationId },
          withCredentials: true,
        },
      );

      if (response.data) {
        const employees = Array.isArray(response.data) ? response.data : [];

        const transformedData = employees.map((attendant) => {
          const profilePicDoc = attendant.documents?.find(
            (doc) => doc.name === "AttendantImage",
          );

          const profilePictureUrl = profilePicDoc
            ? `${config.baseURL}/upload/image?fileName=${profilePicDoc.location}`
            : null;

          return {
            "Staff IDs": attendant.staffCode || "N/A",
            Names: (() => {
              const full =
                `${attendant.firstName || ""} ${attendant.lastName || ""}`.trim();
              return (
                (full.endsWith(" .") ? full.slice(0, -2).trim() : full) || "N/A"
              );
            })(),
            "Vehicle Numbers":
              attendant.vehicle?.VehicleNumber ||
              attendant.vehicle?.name ||
              t("Not Assigned"),
            Routes: attendant.route?.name || t("Not Assigned"),
            "Phone Numbers": attendant.phoneNumber || t("N/A"),
            Gender: attendant.gender || t("N/A"),
            fullData: {
              ...attendant,
              profileImage: profilePictureUrl,
            },
          };
        });

        setAttendantData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching attendants:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t("Failed to fetch attendants"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (values) => {
    setFilterValues(values);
  };

  const handleCreateAttendant = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
  };

  const handleSubmitAttendant = async (formData) => {
    // Refresh the attendants list after successful creation
    await fetchAttendants();
    setShowCreateForm(false);
    setAlert({
      open: true,
      message: t("Attendant created successfully"),
      severity: "success",
    });
  };

  const handleUpdateAttendant = async (formData) => {
    // Refresh the attendants list after successful update
    await fetchAttendants();
    setExpandedRow(null);
    setAlert({
      open: true,
      message: t("Attendant updated successfully"),
      severity: "success",
    });
  };

  const handleDeleteAttendant = async () => {
    if (expandedRow === null) return;

    const attendantToDelete = attendantData[expandedRow];

    try {
      await axios.delete(`${config.baseURL}/employee`, {
        data: {
          employeeId: attendantToDelete.fullData.id,
          organizationId: organizationId,
        },
        withCredentials: true,
      });

      // Refresh the attendants list after successful deletion
      await fetchAttendants();
      setExpandedRow(null);

      setAlert({
        open: true,
        message: t("Attendant deleted successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting attendant:", error);
      setAlert({
        open: true,
        message: error.response?.data?.error || t("Failed to delete attendant"),
        severity: "error",
      });
    }
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCloseExpandedForm = () => {
    setExpandedRow(null);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = [...attendantData];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((attendant) =>
        Object.entries(attendant).some(([key, value]) => {
          // Skip fullData object in search
          if (key === "fullData") return false;
          return String(value)
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        }),
      );
    }

    // Apply route filter
    if (filterValues.routes) {
      filtered = filtered.filter(
        (attendant) => attendant.Routes === filterValues.routes,
      );
    }

    // Apply gender filter
    if (filterValues.gender) {
      filtered = filtered.filter(
        (attendant) => attendant.Gender === filterValues.gender,
      );
    }

    return filtered;
  }, [searchQuery, filterValues, attendantData]);

  // Custom expanded row renderer
  const renderExpandedRow = (row, rowIndex) => {
    return (
      <CreateAttendantForm
        onClose={handleCloseExpandedForm}
        onSubmit={handleUpdateAttendant}
        onDelete={handleDeleteAttendant}
        initialData={row.fullData}
        isEdit={true}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        {/* Alert Snackbar */}
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseAlert}
            severity={alert.severity}
            sx={{ width: "100%" }}
          >
            {alert.message}
          </Alert>
        </Snackbar>

        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title={t("Attendants")}
          add={t("+ Create Attendants")}
          onAddClick={handleCreateAttendant}
        />
        <div className="table_container">
          {showCreateForm && (
            <CreateAttendantForm
              onClose={handleCloseForm}
              onSubmit={handleSubmitAttendant}
              isEdit={false}
            />
          )}

          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            dataDownload={true}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-attendants"
          />

          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              expandedRow={expandedRow}
              onRowClick={handleRowClick}
              renderExpandedRow={renderExpandedRow}
              profileImageColumn="Names"
            />
          </Suspense>
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
